/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import dayjs from 'dayjs';
import { AddressLine } from '../../../../services/als';
import { splitAddress } from '../../../../services/als/splitAddress';
import { AccountAddress } from '../../../azure-b2c/storage';
import {
  DeliveryMonthsPlan,
  FreightCharge,
  MonthlyOrder,
  SaleAgreementPromoProduct,
  SaleAgreementPromoProductSelection,
  SalesAgreementPromoProductSelectionVariant,
  SiteInfo,
} from './types';

export const getFullAddressForCampaign = (address: AddressLine | AccountAddress) => {
  // this is address line from search address api
  if ('UniqueId' in address) {
    const { first, second } = splitAddress(address.FullAddress);
    return {
      name: first,
      fullAddress: second,
    };
  }

  return {
    name: address.name,
    fullAddress: address.fullAddress,
  };
};

/**
 * Divides a total quantity evenly across the months between a given
 * start and end timestamp, distributing any remainder across the first
 * few months to ensure an even spread.
 *
 * @param total - The total quantity to be divided across the months.
 * @param start - The start timestamp in ISO format (e.g., "2024-01-15T00:00:00Z").
 * @param end - The end timestamp in ISO format (e.g., "2024-06-15T00:00:00Z").
 * @returns An array of MonthlyOrder objects, where each object contains
 * the year, month (1-12), and distributed quantity for that month, based
 * on the range of months between the start and end timestamps.
 *
 * @example
 * divideIntoMonths(62, "2024-01-15T00:00:00Z", "2024-06-15T00:00:00Z");
 * // Returns:
 * // [
 * //   { year: 2024, month: 1, quantity: 11 },
 * //   { year: 2024, month: 2, quantity: 11 },
 * //   { year: 2024, month: 3, quantity: 10 },
 * //   { year: 2024, month: 4, quantity: 10 },
 * //   { year: 2024, month: 5, quantity: 10 },
 * //   { year: 2024, month: 6, quantity: 10 }
 * // ]
 */
export const divideIntoMonths = (total: number, start: string, end: string): MonthlyOrder[] => {
  const startDate = dayjs(start);
  const endDate = dayjs(end);

  const monthsBetween: { year: number; month: number }[] = [];
  const totalMonths = endDate.diff(startDate, 'month');

  for (let i = 0; i <= totalMonths; i++) {
    const current = startDate.add(i, 'month');
    monthsBetween.push({
      year: current.year(),
      month: current.month(), // JavaScript months are 0-based
    });
  }

  const baseAmount = Math.floor(total / monthsBetween.length);
  const remainder = total % monthsBetween.length;

  return monthsBetween.map((entry, index) => ({
    year: entry.year,
    month: entry.month,
    quantity: baseAmount + (index < remainder ? 1 : 0),
  }));
};

export const calculateTotalFreightCost = (
  productSelected:
    | SalesAgreementPromoProductSelectionVariant
    | SalesAgreementPromoProductSelectionVariant[],
  freightCharge: FreightCharge,
): number => {
  // Return 0 if freightCharge is null, undefined, or 0
  if (!freightCharge) {
    return 0;
  }

  let totalQuantity = 0;

  if (Array.isArray(productSelected)) {
    // Calculate the total orders quantity across all products
    totalQuantity = productSelected.reduce(
      (sum, product) =>
        sum + (product.orders?.reduce((sum2, order) => sum2 + order.quantity, 0) ?? 0),
      0,
    );
  } else {
    totalQuantity = productSelected.orders?.reduce((sum2, order) => sum2 + order.quantity, 0) ?? 0;
  }

  // Return the total freight cost
  return freightCharge * totalQuantity;
};

export const getPrice = (product: SalesAgreementPromoProductSelectionVariant) => {
  if (product.editedPrice) {
    return product.editedPrice;
  }

  if (product.selectedDeliveryMonthPlan) {
    return product.selectedDeliveryMonthPlan.unitPrice;
  }

  return product.unitPrice;
};

export const getSites = (products: SaleAgreementPromoProduct[]) => {
  const sitesObj = products.reduce<Record<string, SiteInfo>>((carry, product) => {
    carry[product.siteId] = {
      siteId: product.siteId,
      siteName: product.siteName,
      sitePostcode: product.sitePostcode,
    };
    return carry;
  }, {});
  return Object.values(sitesObj).sort((a, b) => a.siteName.localeCompare(b.siteName));
};

export const sortProductsSelected = (products: SalesAgreementPromoProductSelectionVariant[]) => {
  return products.toSorted((a, b) => a.lineNumber - b.lineNumber);
};

export const isLocationInSameIsland = (sitePostcode: number, addressPostcode: number) => {
  const bothInSouthIsland = sitePostcode >= 7000 && addressPostcode >= 7000;
  const bothInNorthIsland = sitePostcode < 7000 && addressPostcode < 7000;
  return bothInSouthIsland || bothInNorthIsland;
};

type ProductParentGroupItem = SaleAgreementPromoProduct & {
  variants: SaleAgreementPromoProduct[];
};

export const transfromProductSelections = (
  products: SaleAgreementPromoProduct[],
): SaleAgreementPromoProductSelection[] => {
  return (
    products
      // This is important to sort first before transform, because the same product can have different line number and variant.
      // To make sure parent product is using the correct lineNumber
      .toSorted((a, b) => a.lineNumber - b.lineNumber)
      .reduce<ProductParentGroupItem[]>((carry, product) => {
        const productTransformed = carry.find((p) => p.itemNumber === product.itemNumber);

        if (productTransformed) {
          // Update existing product's variants with the new variant
          productTransformed.variants.push(product);
        } else {
          // Create new product with initial variant
          carry.push({
            ...product,
            variants: [product],
          });
        }
        return carry;
      }, [])
      // transform the varaint's which support the delivery months grouping
      .map((parentProduct) => {
        return {
          ...parentProduct,
          variants: parentProduct.variants
            .reduce<SalesAgreementPromoProductSelectionVariant[]>((carry, productVariant) => {
              const productVariantTransformed = carry.find(
                (p) =>
                  p.itemNumber === productVariant.itemNumber &&
                  p.productId === productVariant.productId,
              );
              const startDate = dayjs(productVariant.startDate);
              const endDate = dayjs(productVariant.endDate);
              // Note: +1 because the date range is incorrect, need clarify with BE
              const monthPlan = endDate.diff(startDate, 'month') + 1;

              if (productVariantTransformed) {
                const isProductVariantDeliveryMonthDefine =
                  productVariantTransformed?.deliveryMonthPlans?.find(
                    (dp) => dp.numOfMonths === monthPlan,
                  );

                if (isProductVariantDeliveryMonthDefine === undefined) {
                  productVariantTransformed.deliveryMonthPlans.push({
                    numOfMonths: monthPlan,
                    unitPrice: productVariant.unitPrice,
                    startDate: productVariant.startDate,
                    endDate: productVariant.endDate,
                    minTotalQuantity: productVariant.quantity,
                  });
                }
              } else {
                carry.push({
                  ...productVariant,
                  originalPrice: productVariant.unitPrice,
                  deliveryMonthPlans: [
                    {
                      numOfMonths: monthPlan,
                      unitPrice: productVariant.unitPrice,
                      startDate: productVariant.startDate,
                      endDate: productVariant.endDate,
                      minTotalQuantity: productVariant.quantity,
                    },
                  ],
                });
              }

              return carry;
            }, [])
            .map((p) => {
              // sort the delivery months
              p.deliveryMonthPlans = p.deliveryMonthPlans.toSorted(
                (a, b) => a.numOfMonths - b.numOfMonths,
              );
              return p;
            })
            .toSorted((a, b) => a.lineNumber - b.lineNumber),
        };
      })
  );
};

export const selectProductDeliveryMonthPlan = (
  product: SalesAgreementPromoProductSelectionVariant,
  deliveryMonthPlan: DeliveryMonthsPlan,
) => {
  product.orders = divideIntoMonths(0, deliveryMonthPlan.startDate, deliveryMonthPlan.endDate);
  product.selectedDeliveryMonthPlan = deliveryMonthPlan;

  return product;
};

export const getMinMonthPeriods = (
  minPeriods: number | undefined,
  deliveryMonthPlan: DeliveryMonthsPlan | undefined,
) => {
  let minMonthPeriods = minPeriods ?? 0;

  if (deliveryMonthPlan?.numOfMonths) {
    const isGreaterThanDeliveryMonthPlan = minMonthPeriods > deliveryMonthPlan.numOfMonths;

    if (isGreaterThanDeliveryMonthPlan) {
      minMonthPeriods = deliveryMonthPlan.numOfMonths;
    }
  }

  return minMonthPeriods;
};
